import { RouteObject, useRoutes } from "react-router";
import { useAppConfig } from "@ct-react/core";
import { ArticleType } from "@shared/url";
import { allLocales } from "@shared/locale";
import { ProtectedOutlet } from "./bundles/client-ui";
import NewsProvider from "./contexts/news";
import loadable from "@loadable/component";

const HomePage = loadable(
  () => import(/* webpackChunkName: "page-home" */ "./pages/home")
);

const SearchPage = loadable(
  () => import(/* webpackChunkName: "page-search" */ "./pages/search")
);

const OpenArticlePage = loadable(
  () => import(/* webpackChunkName: "page-open-article" */ "./pages/open-article")
);

const NotFoundPage = loadable(
  () => import(/* webpackChunkName: "page-not-found" */ "./pages/not-found")
);

const CartPage = loadable(
  () => import(/* webpackChunkName: "page-checkout-cart" */ "./pages/checkout/cart")
);

const CheckoutOutlet = loadable(
  () => import(/* webpackChunkName: "page-checkout-outlet" */ "./pages/checkout/outlet")
);

const InformationPage = loadable(
  () => import(/* webpackChunkName: "page-checkout-information" */ "./pages/checkout/information")
);

const PaymentPage = loadable(
  () => import(/* webpackChunkName: "page-checkout-payment" */ "./pages/checkout/payment")
);

const CallbackPage = loadable(
  () => import(/* webpackChunkName: "page-checkout-callback" */ "./pages/checkout/callback")
);

const ProfilePage = loadable(
  () => import(/* webpackChunkName: "page-profile" */ "./pages/me/profile")
);

const Gerance = loadable(
  () => import(/* webpackChunkName: "page-gerance" */ "./pages/gerance")
);

const EstimationImmobiliere = loadable(
  () => import(/* webpackChunkName: "page-estimation-immobiliere" */ "./pages/estimation-immobiliere")
);

const Renovation = loadable(
  () => import(/* webpackChunkName: "page-renovation" */ "./pages/renovation")
);

const About = loadable(
  () => import(/* webpackChunkName: "page-about" */ "./pages/about")
);

const Nendaz = loadable(
  () => import(/* webpackChunkName: "page-nendaz" */ "./pages/nendaz")
);

const News = loadable(
  () => import(/* webpackChunkName: "page-news" */ "./pages/news")
);

const Contact = loadable(
  () => import(/* webpackChunkName: "page-contact" */ "./pages/contact")
);

const SingleNew = loadable(
  () => import(/* webpackChunkName: "page-single-new" */ "./pages/single-new")
);

const Wishlist = loadable(
  () => import(/* webpackChunkName: "page-wishlist" */ "./pages/wishlist")
);

export const routesConfig = (withLogin: boolean = false): RouteObject[] => ([
  {
    element: (
      <NewsProvider>
        <HomePage />
      </NewsProvider>
    ),
    index: true,
    data: { search: { displayable: false } }
  },
  {
    path: "location",
    children: [
      {
        path: "courte-duree",
        children: [
          {
            path: "",
            index: true,
            element: <SearchPage type={ArticleType.RENTAL} />,
            data: { search: { withSearchParams: ArticleType.RENTAL, targeted: true } },
          },
          {
            path: ":slug",
            element: <OpenArticlePage type={ArticleType.RENTAL} />,
            data: { search: { displayable: false } }
          }
        ]
      },
      {
        path: "saison",
        children: [
          {
            path: "",
            index: true,
            element: <SearchPage type={ArticleType.SEASONAL} />,
            data: { search: { withSearchParams: ArticleType.SEASONAL, targeted: true } },
          },
          {
            path: ":slug",
            element: <OpenArticlePage type={ArticleType.SEASONAL} />,
            data: { search: { displayable: false } }
          }
        ]
      },
      {
        path: "annuel",
        children: [
          {
            path: "",
            index: true,
            element: <SearchPage type={ArticleType.ANNUAL} />,
            data: { search: { withSearchParams: ArticleType.ANNUAL, targeted: true } },
          },
          {
            path: ":slug",
            element: <OpenArticlePage type={ArticleType.ANNUAL} />,
            data: { search: { displayable: false } }
          }
        ]
      }
    ]
  },
  {
    path: "vente",
    children: [
      {
        path: "",
        index: true,
        element: <SearchPage type={ArticleType.SALE} />,
        data: { search: { withSearchParams: ArticleType.SALE, targeted: true } },
      },
      {
        path: ":slug",
        element: <OpenArticlePage type={ArticleType.SALE} />,
        data: { search: { displayable: false } }
      }
    ]
  },
  {
    path: "gerance",
    element: <Gerance />,
    data: { search: { displayable: false } }
  },
  {
    path: "estimation-immobiliere",
    element: <EstimationImmobiliere />,
    data: { search: { displayable: false } }
  },
  {
    path: "renovation",
    element: <Renovation />,
    data: { search: { displayable: false } }
  },
  {
    path: "a-propos",
    element: <About />,
    data: { search: { displayable: false } }
  },
  {
    path: "nendaz",
    element: <Nendaz />,
    data: { search: { displayable: false } }
  },
  {
    path: "actualites",
    children:[
      {
        path: "",
        index: true,
        element: (
          <NewsProvider>
            <News />
          </NewsProvider>
        ),
        data: { search: { displayable: false } },
      },
      {
        path: ":slug",
        element: (
          <NewsProvider>
            <SingleNew />
          </NewsProvider>
        ),
        data: { search: { displayable: false } },
      }
    ]
  },
  {
    path: "contact",
    element: <Contact />,
    data: { search: { displayable: false } }
  },
  {
    path: "checkout",
    element: <CheckoutOutlet />,
    data: { search: { displayable: false } },
    children: [
      {
        path: "",
        index: true,
        element: <CartPage />
      },
      {
        path: "information",
        element: <InformationPage />
      },
      {
        path: "payment",
        children: [
          {
            path: "",
            index: true,
            element: <PaymentPage />
          },
          {
            path: "success",
            element: <CallbackPage succeeded={true} />
          },
          {
            path: "cancellation",
            element: <CallbackPage succeeded={false} />
          }
        ]
      }
    ]
  },
  ...(!withLogin ? [] : [
    {
      path: "me",
      element: <ProtectedOutlet />,
      data: { search: { displayable: false } },
      children: [
        {
          path: "profile",
          index: true,
          element: <ProfilePage />
        }
      ]
    }
  ]),
  {
    path: "wishlist",
    element: <Wishlist/>,
    data: { search: { displayable: false } }
  },
  {
    path: "*",
    element: <NotFoundPage />,
    data: { search: { displayable: false } }
  }
]);

const LocaleRoutes = () => {

  const { options: { withLogin } } = useAppConfig();

  return useRoutes([
    ...allLocales.map(locale => ({
      path: locale.basename,
      children: routesConfig(withLogin) as RouteObject[]
    }))
  ]);

}

export default LocaleRoutes;
